import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DashboardModal from '@/components/dashboard-modal';
import TabbedImageUpload from './TabbedImageUpload';
import ImagePreviewStep from './ImagePreviewStep';
import { ImageUploadProps } from './image-upload/types';
import ActionButton from '../ActionButton';
import { WandSparkles, Upload } from 'lucide-react';
import { useImageUpload } from './image-upload/useImageUpload';
import { useAvatarGeneration } from './TabbedImageUpload/useAvatarGeneration';
import { useCoverImageGeneration } from '@/hooks/content-generator/useCoverImageGeneration';
import { useWorkbookImageGeneration } from '@/hooks/content-generator/use-workbook-images';
import { ColorVariant } from '@/lib/colors';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';
import { UploadDropzoneRef } from './UploadDropzone';

interface TabbedImageUploadModalProps extends ImageUploadProps {
  autoUpload?: boolean;
  showProgress?: boolean;
  mode?: 'avatar' | 'cover' | 'workbook-image'; // New prop to distinguish between avatar, cover, and workbook image generation
  workbookId?: string;
  pageId?: string;
  initialDescription?: string; // NEW: Pre-fill description with AI-generated prompt
  skipPreview?: boolean; // NEW: Skip preview step for seamless AI Magic flows
  isOnboarding?: boolean; // NEW: Indicates if this is used in onboarding flow (affects button text)
  showImportTab?: boolean; // NEW: Controls whether the Import tab is shown
  onDirectImageImport?: (imageUrl: string, altText?: string) => void; // NEW: Direct URL insertion callback for editor contexts
}

const TabbedImageUploadModal: React.FC<TabbedImageUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  title = 'Add Profile Photo',
  description,
  maxSizeMB = 10, // 10MB - WebP conversion handles optimization
  allowedTypes = ['image/jpeg', 'image/png', 'image/webp'],
  aspectRatio = 1,
  previewSize = 'md',
  accentColor = 'blue',
  autoUpload = false,
  showProgress = true,
  mode = 'avatar', // Default to avatar mode
  workbookId,
  pageId,
  initialDescription = '', // NEW: Initial description prop
  skipPreview = false, // NEW: Skip preview prop
  isOnboarding,
  showImportTab = false,
  onDirectImageImport
}) => {
  const location = useLocation();
  
  // Auto-detect onboarding context if not explicitly provided
  const autoDetectedOnboarding = location.pathname === '/welcome' || location.pathname === '/onboard';
  const effectiveIsOnboarding = isOnboarding ?? autoDetectedOnboarding;
  
  const [currentStep, setCurrentStep] = useState<'input' | 'preview'>('input');
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null); // Store the uploaded file for final submission
  const [activeTab, setActiveTab] = useState<'import' | 'generate' | 'upload'>('generate');
  const [imageDescription, setImageDescription] = useState(initialDescription); // 🎯 INITIALIZE with prop
  const [imageStyle, setImageStyle] = useState<string>(mode === 'avatar' ? 'photo' : 'style-photo'); // 🎯 CHANGED: Default to 'style-photo' for cover images
  const [imageWidth, setImageWidth] = useState<string>('1024');
  const [imageHeight, setImageHeight] = useState<string>('1024');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dropzoneRef = useRef<UploadDropzoneRef>(null);
  
  const {
    state: uploadState,
    handleFileSelection,
    handleClose,
    handleUpload,
    triggerFileInput
  } = useImageUpload(onUpload, onClose, fileInputRef, { 
    autoUpload, 
    showProgress,
    autoClose: false // Disable auto-close to prevent interference with preview step
  });
  
  const { generateAvatar, isGenerating: isGeneratingAvatar } = useAvatarGeneration();
  const { generateCoverImage, isGenerating: isGeneratingCover } = useCoverImageGeneration();
  const { generateWorkbookImage, isGenerating: isGeneratingWorkbook } = useWorkbookImageGeneration();
  
  // 🎯 ADD: Credit checking hooks for upfront validation
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();

  // Add modal-level paste event listener
  useEffect(() => {
    if (!isOpen) return;

    const handleModalPaste = (e: ClipboardEvent) => {
      // Only handle paste if we're on the upload tab and there's no selected file
      if (activeTab === 'upload' && !uploadState.selectedFile && dropzoneRef.current) {
        console.log('[TabbedImageUploadModal] Modal paste event detected, delegating to dropzone');
        dropzoneRef.current.handlePasteEvent(e);
      }
    };

    // Listen for paste events on the document when modal is open
    document.addEventListener('paste', handleModalPaste);

    return () => {
      document.removeEventListener('paste', handleModalPaste);
    };
  }, [isOpen, activeTab, uploadState.selectedFile]);
  
  const handleGeneratedImageUpload = async (imageUrl: string) => {
    try {
      console.log(`[TabbedImageUploadModal] Starting upload for ${mode} mode with imageUrl:`, imageUrl);
      
      // Convert image URL to File object
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const fileName = `generated-${mode}-${Date.now()}.webp`;
      const file = new File([blob], fileName, { type: 'image/webp' });
      
      // For workbook mode, attach generation metadata to file for parent handler
      if (mode === 'workbook-image' && workbookId) {
        console.log('[TabbedImageUploadModal] Workbook mode - attaching generation metadata to file');
        
        // Attach generation metadata to file object for parent handler
        Object.defineProperty(file, 'generationMetadata', {
          value: {
            altText: imageDescription || 'Generated workbook image',
            isGenerated: true,
            generationPrompt: imageDescription,
          },
          enumerable: false,
          writable: false
        });
        
        // Store the file for later submission and use original URL for preview
        setUploadedFile(file);
        setUploadedImageUrl(imageUrl);
      } else {
        // For other modes, we need to upload but NOT call parent's onUpload yet
        // We'll call it later when user clicks "Done" to prevent premature modal close
        
        // Store the file for later submission and use original URL for preview
        setUploadedFile(file);
        setUploadedImageUrl(imageUrl);
      }
      
      console.log('[TabbedImageUploadModal] Final image URL for preview:', imageUrl);
      
      // Check if we should skip preview step for seamless AI Magic flows
      if (skipPreview) {
        console.log('[TabbedImageUploadModal] Skipping preview step - calling parent onUpload directly');
        // Skip preview and finalize upload immediately using the file we just created
        try {
          await onUpload(file); // Use the file we just created, not uploadedFile state
        } catch (error) {
          console.error('[TabbedImageUploadModal] Error in direct upload:', error);
        }
        onClose();
        return;
      }
      
      // Move to preview step without calling parent onUpload (prevents premature close)
      setCurrentStep('preview');
      console.log('[TabbedImageUploadModal] Moved to preview step');
    } catch (error) {
      console.error('[TabbedImageUploadModal] Error uploading generated image:', error);
    }
  };

  // Remove the complex handleImportedImageUpload function
  // All imports now use direct URL operations (no more unnecessary re-uploads)

  // Handler for image generation
  const handleImageGeneration = async () => {
    // 🎯 UPFRONT CREDIT CHECK - Prevent operation if no credits available
    if (usageData.remainingBalance === 0) {
      console.log('[TabbedImageUploadModal] Insufficient credits - blocking image generation and showing modal');
      openOutOfCreditsModal();
      return; // Clean early exit - no cascading errors
    }

    console.log(`[TabbedImageUploadModal] Credits available (${usageData.remainingBalance}) - proceeding with image generation`);
    
    try {
      let imageUrl: string | null = null;
      
      if (mode === 'avatar') {
        imageUrl = await generateAvatar(imageDescription || 'A professional profile photo', imageStyle as any);
        if (imageUrl) {
          await handleGeneratedImageUpload(imageUrl);
        }
      } else if (mode === 'cover') {
        imageUrl = await generateCoverImage(imageDescription || 'A beautiful landscape cover image', imageStyle);
        if (imageUrl) {
          await handleGeneratedImageUpload(imageUrl);
        }
      } else if (mode === 'workbook-image') {
        // For workbook images, use direct hook call with dimensions
        const styleForApi = imageStyle.replace('style-', ''); // Remove 'style-' prefix for API
        imageUrl = await generateWorkbookImage(
          imageDescription || 'A professional workbook image', 
          styleForApi, 
          imageWidth, 
          imageHeight
        );
        if (imageUrl) {
          await handleGeneratedImageUpload(imageUrl);
        }
      }
    } catch (error) {
      console.error('Error generating image:', error);
    }
  };
  
  // Update image settings
  const updateImageSettings = (description: string, style: string) => {
    setImageDescription(description);
    setImageStyle(style);
  };
  
  // Update parent component when tab changes
  const handleTabChange = (tab: 'import' | 'generate' | 'upload') => {
    setActiveTab(tab);
  };

  // Handle "Try Again" from preview step
  const handleTryAgain = () => {
    setCurrentStep('input');
    setUploadedImageUrl(null);
    setUploadedFile(null); // Reset uploaded file state
    // Reset any generation states if needed
  };

  // Handle "Done" from preview step
  const handleDone = async () => {
    // Now call the parent's onUpload to finalize the upload and close modal
    if (uploadedFile) {
      try {
        console.log('[TabbedImageUploadModal] Finalizing upload with parent onUpload');
        await onUpload(uploadedFile);
      } catch (error) {
        console.error('[TabbedImageUploadModal] Error in final upload:', error);
      }
    }
    onClose();
  };

  // Handle modal close - always allow closing
  const handleModalClose = () => {
    onClose();
  };

  // Handle primary action button click
  const handleActionClick = () => {
    if (activeTab === 'upload') {
      if (uploadState.selectedFile) {
        if (uploadState.uploadStatus !== 'uploading') {
          handleUpload();
        }
      } else {
        triggerFileInput();
      }
    } else {
      handleImageGeneration();
    }
  };

  // Loading state based on active tab and mode
  const isLoading = 
    (activeTab === 'upload' && uploadState.uploadStatus === 'uploading') ||
    (activeTab === 'generate' && (isGeneratingAvatar || isGeneratingCover || isGeneratingWorkbook));
    
  // Reset modal state when opened
  useEffect(() => {
    if (isOpen) {
      // Reset to input step when modal opens
      setCurrentStep('input');
      setUploadedImageUrl(null);
      setUploadedFile(null);
      setActiveTab('generate');
      setImageDescription(initialDescription);
    }
  }, [isOpen, initialDescription]);

  // Auto-close when upload is successful (only for regular upload tab, not generate tab)
  useEffect(() => {
    if (uploadState.uploadStatus === 'success' && activeTab === 'upload') {
      const timer = setTimeout(() => {
        handleClose();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [uploadState.uploadStatus, activeTab]);
  
  // Determine button text based on active tab
  const buttonText = activeTab === 'upload' 
    ? (uploadState.selectedFile ? 'Upload' : 'Select File') 
    : 'Generate Image';
  
  // Determine button icon based on active tab
  const buttonIcon = activeTab === 'upload' ? <Upload size={16} /> : <WandSparkles size={16} />;

  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={title}
      description={description}
      size="lg"
    >
      {currentStep === 'input' ? (
                  <TabbedImageUpload
            onUpload={onUpload}
            onClose={onClose}
            maxSizeMB={maxSizeMB}
            allowedTypes={allowedTypes}
            aspectRatio={aspectRatio}
            previewSize={previewSize}
            accentColor={accentColor as ColorVariant}
            onTabChange={handleTabChange}
            // REMOVED: onImportedImageUpload - no longer needed
            showImportTab={showImportTab}
            fileInputRef={fileInputRef}
            triggerFileInput={triggerFileInput}
            setImageDescription={setImageDescription}
            setImageStyle={setImageStyle}
            setImageWidth={setImageWidth}
            setImageHeight={setImageHeight}
            autoUpload={autoUpload}
            mode={mode}
            workbookId={workbookId}
            pageId={pageId}
            initialDescription={initialDescription}
            isOnboarding={effectiveIsOnboarding}
            ref={dropzoneRef}
            // Footer button props - Hide buttons for Import tab since images are imported directly
            showFooterButtons={!showImportTab || activeTab !== 'import'}
            footerCancelText={effectiveIsOnboarding ? 'Skip' : 'Cancel'}
            footerActionText={buttonText}
            footerActionIcon={buttonIcon}
            footerActionVariant={activeTab === 'generate' ? 'purple' : accentColor as ColorVariant}
            footerActionClassName={activeTab === 'generate' ? "bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600" : ""}
            onFooterCancel={handleModalClose}
            onFooterAction={handleActionClick}
            isFooterActionDisabled={isLoading}
            isFooterActionLoading={isLoading}
            onDirectImageImport={onDirectImageImport} // UNIFIED: All imports use direct URL operations
          />
      ) :
        uploadedImageUrl && (
          <ImagePreviewStep
            imageUrl={uploadedImageUrl}
            mode={mode}
            aspectRatio={aspectRatio}
            onTryAgain={handleTryAgain}
            onDone={handleDone}
          />
        )
      }
    </DashboardModal>
  );
};

export default TabbedImageUploadModal;
